import { AxiosHeaders, isAxiosError } from 'axios'

export interface ApiProblem {
  type: string
  title: string
  status: number
  detail: string
  instance: string
  code: string
  errors?: Record<string, string>
  [extension: string]: unknown
}

/** Only a matching HTTP Problem Details response can drive domain-specific UI decisions. */
export function readApiProblem(error: unknown): ApiProblem | undefined {
  if (!isAxiosError(error) || !error.response) return undefined
  const { status, data, headers } = error.response
  const contentType = headers instanceof AxiosHeaders ? headers.get('content-type')
    : Object.entries(headers ?? {}).find(([key]) => key.toLowerCase() === 'content-type')?.[1]
  if (typeof contentType !== 'string' || contentType.split(';', 1)[0].trim().toLowerCase() !== 'application/problem+json') return undefined
  if (status < 400 || status > 599 || data === null || typeof data !== 'object' || Array.isArray(data)) return undefined
  const body = data as Record<string, unknown>
  if (!Number.isInteger(body.status) || body.status !== status
    || typeof body.type !== 'string' || typeof body.title !== 'string' || !body.title.trim()
    || typeof body.detail !== 'string' || typeof body.instance !== 'string' || !body.instance
    || typeof body.code !== 'string' || !/^[A-Z][A-Z0-9]*-(?:HTTP-)?[0-9]{3,6}$/.test(body.code)) return undefined
  try {
    if (new URL(body.type).protocol !== 'https:') return undefined
  } catch { return undefined }
  if (body.errors !== undefined && (body.errors === null || typeof body.errors !== 'object'
    || Array.isArray(body.errors) || Object.values(body.errors).some(value => typeof value !== 'string'))) return undefined
  return body as unknown as ApiProblem
}

/**
 * The message a problem's `errors` map carries for one request field, keyed by JSON pointer
 * (`/reason`). Undefined when the failure is not a problem, or names no message for that field.
 */
export function readProblemFieldError(error: unknown, pointer: string): string | undefined {
  const message = readApiProblem(error)?.errors?.[pointer]
  return message?.trim() ? message : undefined
}
